/**
 * The guided run: five waves with their parameters bent to teach five verbs.
 *
 * A tutorial in this codebase cannot be a stack of modals over a game that
 * would have gone on regardless. The settlement already has a director whose
 * whole job is deciding what a wave is made of and where it walks in from, and
 * the honest way to teach a verb is to bend that decision so that **the thing
 * the player was just told to do is the thing that then happens to them**: you
 * are told to lay a wall across the Weeping Ford, and the wave that comes walks
 * in at the Weeping Ford and breaks on it. A card that says "now build a
 * Wellspring" over a wave the director planned an hour ago teaches nothing, and
 * every screen in this game would show it up.
 *
 * So this module is the *bend*, and `ui/tutorial.ts` is only the card that says
 * what it is. Everything here is a pure read of the world plus one written
 * number, `World.tutorialStep`:
 *
 *   0        no lessons — an ordinary run, and every function here is a no-op
 *   1 … 5    the guided wave now being planned or fought, one per verb
 *   6        graduated: the lessons are behind this run and nothing is bent
 *
 * It advances **in the simulation**, at the end of a siege, and never from the
 * card. That is the anti-soft-lock rule made structural: a lesson cannot gate
 * progress on an action the player can no longer afford to take, because no
 * lesson gates anything at all. The wave comes when the clock runs out whether
 * the lesson was learned or ignored, the step advances when the wave ends, and
 * a player who does nothing for five waves arrives at wave six in an ordinary,
 * playable, survivable run. `tests/tutorial.test.ts` plays exactly that.
 *
 * The one editorial rule is the Codex's, and it is inherited on purpose: this
 * only ever *shapes what the game already reads*. There is no tutorial-only
 * unit, no tutorial-only gate, no tutorial-only phase. A guided wave is an
 * ordinary wave whose budget, gate, faction and composition were chosen for a
 * reason — which is why skipping mid-run leaves a real game behind rather than
 * a game that has to be repaired.
 */

import { unitType, unitTypeIndex } from '../../data/units.ts';
import { faction } from '../../data/factions.ts';
import { UF, WallKind } from '../types.ts';
import type { World } from '../world.ts';
import { BUILD_PHASE_SECONDS } from '../constants.ts';

/** How many guided waves there are. Past this, the run is ordinary. */
export const TUTORIAL_WAVES = 5;

export type LessonId = 'build' | 'wall' | 'muster' | 'command' | 'cast';

export interface GuidedWave {
  id: LessonId;
  /**
   * The invader faction this lesson's wave is drawn from, by id.
   *
   * Fixed rather than rolled, because a lesson is a sentence about a specific
   * thing walking towards you and the scouting panel prints the name. All four
   * are ordinary invader factions the director would have picked anyway.
   */
  factionId: string;
  /** Share of the wave budget this wave would ordinarily have had. */
  budgetMul: number;
  /** How many gates it walks in from. */
  gates: number;
  /**
   * Seconds of build phase before this wave, as a floor. `0` leaves the
   * director's own clock alone.
   */
  buildFloor: number;
  /** What the wave is made of, as roles rather than as unit ids. */
  parts: Array<{ role: Role; share: number }>;
  /** One line for the card, saying what this wave is and why. */
  brief: string;
}

/**
 * A role is a predicate over the incoming faction's own roster.
 *
 * Named roles rather than unit ids, for the reason the Codex gives for
 * `ABILITY_NOTES`: a lesson that named `gutter_swarm` would be a lesson that
 * quietly stopped meaning anything the day somebody re-costed the Gutter. A
 * role is resolved against whatever that faction actually fields today, and if
 * a faction has nothing in a role the share falls back to its backbone.
 */
export type Role = 'backbone' | 'shot' | 'swarm';

const LESSONS: GuidedWave[] = [
  {
    id: 'build',
    factionId: 'gutter',
    // A probe. The first wave a new player ever sees should be survivable by a
    // settlement that spent its whole first five minutes laying out farms —
    // which is what the card told them to do.
    budgetMul: 0.5,
    gates: 1,
    buildFloor: 0,
    parts: [{ role: 'backbone', share: 1 }],
    brief: 'A few of them, from one gate. Not enough to hurt a town that is standing.',
  },
  {
    id: 'wall',
    factionId: 'ironfang',
    // Nothing that flies, saps or burrows: this is the one wave in the game
    // that is *allowed* to break on a wall, because that is the lesson.
    budgetMul: 0.7,
    gates: 1,
    buildFloor: 0,
    parts: [{ role: 'backbone', share: 1 }],
    brief: 'They come at the gate you walled, on foot, and nothing in them can go over or under stone.',
  },
  {
    id: 'muster',
    factionId: 'ironfang',
    /**
     * The long one, and the only lesson given room of its own.
     *
     * Muster is the verb a new player fails first — see the write-up in
     * COORDINATION.md. It is the only one of the five whose door is not in the
     * build menu (you place a barracks, wait for it to be *finished*, click it,
     * and find the drill button inside its panel), the only one with four
     * separate silent refusals behind it — no trainer, no grown adults, no
     * beds, no purse — and the only one whose absence is invisible for three
     * waves and then fatal. So it gets the longest build phase in the run and
     * a wave a wall on its own cannot answer: shot, standing off, shooting
     * whatever is behind the stone.
     */
    budgetMul: 0.85,
    gates: 1,
    buildFloor: BUILD_PHASE_SECONDS * 1.6,
    parts: [
      { role: 'backbone', share: 0.6 },
      { role: 'shot', share: 0.4 },
    ],
    brief: 'Bowmen among them. They will stand off your wall and shoot over it; stone does not shoot back.',
  },
  {
    id: 'command',
    factionId: 'rotmoot',
    // Two gates, and the second is chosen to be the one the player did *not*
    // wall. A company that sits where it was raised loses this wave.
    budgetMul: 0.85,
    gates: 2,
    buildFloor: 0,
    parts: [{ role: 'backbone', share: 1 }],
    brief: 'Two gates this time, and only one of them is the one you fortified.',
  },
  {
    id: 'cast',
    factionId: 'rotmoot',
    // One gate, one knot. A Sovereign Power is worth most against a crowd, and
    // this is a crowd.
    budgetMul: 1,
    gates: 1,
    buildFloor: 0,
    parts: [{ role: 'swarm', share: 1 }],
    brief: 'One gate, and all of them through it at once. That press is what a Power is for.',
  },
];

/** The lesson now in force, or null for an ordinary run. */
export function guidedLesson(w: World): GuidedWave | null {
  const n = w.tutorialStep;
  if (n < 1 || n > TUTORIAL_WAVES) return null;
  return LESSONS[n - 1];
}

/** Every lesson, in order. The card reads this; nothing writes it. */
export function tutorialLessons(): readonly GuidedWave[] {
  return LESSONS;
}

/**
 * Begin the guided run. Refused anywhere it would be a cheat rather than a
 * lesson: a bent wave is a *weaker* wave, so this may only ever be turned on
 * before the first horn of a standalone settlement.
 */
export function beginGuidedRun(w: World, at = 1): boolean {
  if (w.battle || w.campaign || w.config.homeCity) return false;
  if (w.wave > 0 || w.gameOver) return false;
  /**
   * `at` is which lesson to open on, and it is not a cheat door: a run that
   * starts at the muster lesson gets the muster lesson's *wave* as its first
   * wave, which is a bigger wave than the build lesson's, and it gets three
   * lessons instead of five. Somebody who only wants to be shown the muster
   * should be able to ask for it, and the screenshot harness asks for each of
   * the five by name.
   */
  w.tutorialStep = Math.max(1, Math.min(TUTORIAL_WAVES, Math.floor(at) || 1));
  return true;
}

/** Leave the lessons. Idempotent, and legal at any moment. */
export function endGuidedRun(w: World): void {
  w.tutorialStep = 0;
}

/**
 * A wave has ended: move to the next lesson, and give the muster its room.
 *
 * Called from `endSiege` *before* the next wave is planned, so the plan that
 * follows is the plan for the lesson the player is about to be taught.
 * Graduating writes `TUTORIAL_WAVES + 1` rather than 0, because "this run was
 * the guided one and it is done" and "this run never had lessons" are different
 * facts and the card needs to tell them apart.
 */
export function advanceGuidedRun(w: World): void {
  if (w.tutorialStep < 1 || w.tutorialStep > TUTORIAL_WAVES) return;
  w.tutorialStep++;
  const next = guidedLesson(w);
  if (next && next.buildFloor > w.phaseT) w.phaseT = next.buildFloor;
}

/**
 * Bend the wave the director has just planned into the wave this lesson needs.
 *
 * Deliberately the *last* thing `planNextWave` does, and deliberately a
 * rewrite rather than a set of hooks threaded through it: one line in the
 * chokepoint, and every rule about what a guided wave is lives here where it
 * can be read in one sitting. The doctrine and counter spends that ran before
 * this are thrown away — they cost a fixed number of draws off `rngDirector`,
 * so the stream stays deterministic either way.
 */
export function shapeGuidedWave(w: World): void {
  const lesson = guidedLesson(w);
  if (!lesson) return;

  w.incomingFaction = lesson.factionId;
  // No Nightfall, no Blood Moon, no Famine Wind while somebody is learning
  // what a wall is. Every modifier is a rule the player has not been told yet.
  w.incomingModifiers = [];
  w.incomingBudget = Math.max(20, w.incomingBudget * lesson.budgetMul);
  w.incomingGates = guidedGates(w, lesson);

  const roster = faction(lesson.factionId).roster
    .map((id) => unitTypeIndex(id))
    .filter((i) => unitType(i).tier <= 1);
  const usable = roster.length > 0 ? roster : [unitTypeIndex(faction(lesson.factionId).roster[0])];

  const comp = new Map<number, number>();
  for (const part of lesson.parts) {
    const pool = inRole(usable, part.role);
    const idx = pool[0];
    const t = unitType(idx);
    const spend = w.incomingBudget * part.share;
    const n = Math.max(2, Math.round(spend / Math.max(1, t.threat)));
    comp.set(idx, (comp.get(idx) ?? 0) + n);
  }

  w.incomingComposition = [...comp.entries()]
    .map(([typeIdx, count]) => ({ typeIdx, count }))
    .sort((a, b) => unitType(b.typeIdx).threat * b.count - unitType(a.typeIdx).threat * a.count);
}

/**
 * Which of the faction's units fills a role.
 *
 * `pool[0]` is taken rather than a random draw on purpose: a guided wave is a
 * sentence the card has already printed ("bowmen among them"), and a roll that
 * sometimes came up without any would make the card a liar. The fallback chain
 * ends at the whole roster, so a faction with no shot at all still fields
 * *something* rather than an empty wave that ends the instant it begins.
 */
function inRole(roster: number[], role: Role): number[] {
  const plain = (i: number): boolean =>
    (unitType(i).flags & (UF.Ranged | UF.Flying | UF.Sapper | UF.Siege | UF.Caster | UF.Champion)) === 0 &&
    unitType(i).ability !== 'burrow';
  let out: number[];
  switch (role) {
    case 'shot':
      out = roster.filter((i) => (unitType(i).flags & UF.Ranged) !== 0);
      break;
    case 'swarm':
      // The cheapest body the faction owns, which is what makes a knot.
      out = roster.filter(plain).sort((a, b) => unitType(a).threat - unitType(b).threat);
      break;
    default:
      out = roster.filter(plain);
      break;
  }
  if (out.length === 0) out = roster.filter(plain);
  if (out.length === 0) out = roster;
  return out;
}

/**
 * The gates a guided wave walks in from.
 *
 * Lessons two and three send it at **the gate the player actually walled**,
 * which is the whole of the wall lesson: a wall you built somewhere the horde
 * never goes is a wall you learned nothing from. The command lesson sends half
 * of it there and half at the far side, so the company has to be moved. A town
 * with no wall at all falls back to the director's own shuffle, which is what
 * an ordinary wave would have done — never a refusal, never a stall.
 */
function guidedGates(w: World, lesson: GuidedWave): number[] {
  const planned = w.incomingGates.length > 0 ? w.incomingGates : [0];
  const walled = walledGate(w);
  const first = walled >= 0 && lesson.id !== 'build' ? walled : planned[0];
  if (lesson.gates <= 1) return [first];
  const away = oppositeGate(w, first);
  return away === first ? [first] : [first, away];
}

/**
 * Which gate the player has fortified against, or −1 if none.
 *
 * Measured rather than remembered: walk the straight line from each gate to the
 * Hearth and count the player's wall and gate tiles on it. That is the same
 * question the horde's flow field answers expensively, asked cheaply, and it
 * needs no new state — which matters, because a remembered "the gate you
 * walled" would be a derived cache that decides what happens, and this codebase
 * calls that state.
 */
export function walledGate(w: World): number {
  let best = -1;
  let bestScore = 0;
  for (let gi = 0; gi < w.map.gates.length; gi++) {
    const g = w.map.gates[gi];
    const dx = w.map.hearthX - g.x;
    const dy = w.map.hearthY - g.y;
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    if (steps <= 0) continue;
    let hits = 0;
    for (let s = 0; s <= steps; s++) {
      const x = Math.round(g.x + (dx * s) / steps);
      const y = Math.round(g.y + (dy * s) / steps);
      if (w.map.wall[w.map.idx(x, y)] !== WallKind.None) hits++;
    }
    if (hits > bestScore) {
      bestScore = hits;
      best = gi;
    }
  }
  return best;
}

/** The gate furthest round the rim from this one. */
function oppositeGate(w: World, from: number): number {
  const n = w.map.gates.length;
  if (n <= 1) return from;
  return (from + (n >> 1)) % n;
}
